import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";

import { post } from "api";
import type { components } from "api/generated";
import type { Models } from "api/types";
import {
  PAYMENT_UPDATED_PARAM,
  UPDATED_MEMBERSHIP_ID_PARAM,
} from "constants/index";
import { buildPlatformUrl } from "utils/functions";

const isNativeApp = Capacitor.isNativePlatform();

// The maximum amount of times a subscription payment will be attempted. At the moment, there is the initial payment attempt and 3 retries.
// see: https://dashboard.stripe.com/revenue-recovery/retries
const MAX_PAYMENT_RETRIES = 4;

export const getDunningDays = (dunningStage: number) => {
  let addedDunningDays = 0;

  switch (dunningStage) {
    case 1:
      addedDunningDays = 7;
      break;
    case 2:
      addedDunningDays = 14;
      break;
    case 3:
      addedDunningDays = 21;
      break;
  }

  return addedDunningDays;
};

export const getRemainingPaymentAttempts = (dunningStage: number) => {
  return MAX_PAYMENT_RETRIES - dunningStage;
};

export const handleUpdatePayment = async (
  membershipId: string,
  platform: components["schemas"]["PlatformModel"],
  onNativeBrowserFinished: () => void
) => {
  const baseUrl = buildPlatformUrl(
    platform?.scheme,
    platform?.subdomain,
    platform?.host
  );

  if (isNativeApp) {
    Browser.addListener("browserFinished", async () => {
      Browser.removeAllListeners();
      onNativeBrowserFinished();
    });

    handleUpdatePaymentOnCapacitor(membershipId, baseUrl);
  } else {
    handleUpdatePaymentOnBrowser(membershipId, baseUrl);
  }
};

const handleUpdatePaymentOnCapacitor = async (
  membershipId: string,
  baseUrl: string
): Promise<void> => {
  const response = await post(
    "/platform-user-memberships/{platformUserMembershipId}/create-stripe-update-session",
    {
      platformUserMembershipId: membershipId,
      successUrl: `${baseUrl}/stripe-landing/close-browser?successfulPayment=true`,
      cancelUrl: `${baseUrl}/stripe-landing/close-browser?successfulPayment=false`,
    }
  );

  await Browser.open({ url: response.stripeSession.url });
};

const handleUpdatePaymentOnBrowser = async (
  membershipId: string,
  baseUrl: string
) => {
  const response = await post(
    "/platform-user-memberships/{platformUserMembershipId}/create-stripe-update-session",
    {
      platformUserMembershipId: membershipId,
      successUrl: `${baseUrl}/stripe-landing?${PAYMENT_UPDATED_PARAM}=1&${UPDATED_MEMBERSHIP_ID_PARAM}=${membershipId}`,
      cancelUrl: `${baseUrl}/stripe-landing?${PAYMENT_UPDATED_PARAM}=0`,
    }
  );

  if (response && response.stripeSession.url) {
    window.location.href = response.stripeSession.url;
  } else {
    console.log("error ocurred");
  }
};

export const canUserReactivateMembership = (
  userMemberships: Models<"PlatformUserMembershipModel">[],
  membershipId: string,
  platformUserMembershipId: string
) => {
  return !!(
    userMemberships
      .filter(
        ({ canceled, willCancelAt, dunningStage, membership }) =>
          membership.id === membershipId &&
          (canceled || (!dunningStage && willCancelAt))
      )
      .reduce(
        (
          d1: Models<"PlatformUserMembershipModel">,
          d2: Models<"PlatformUserMembershipModel">
        ) => {
          return d1.willCancelAt &&
            d2.willCancelAt &&
            new Date(d1.willCancelAt) > new Date(d2.willCancelAt)
            ? d1
            : d2;
        },
        {} as Models<"PlatformUserMembershipModel">
      ).platformUserMembershipId === platformUserMembershipId
  );
};
